import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Paperclip, FileText, Loader2 } from 'lucide-react';

const ChatPanel = ({
  isDark, section, chatHistory = [], onSend, onClose
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedText, setAttachedText] = useState(null);
  const fileRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [section?.id]);

  const handleFileAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const text = await file.text();
      setAttachedFile(file.name);
      setAttachedText(text.slice(0, 5000));
    } catch {
      // For binary files, just note the name
      setAttachedFile(file.name);
      setAttachedText(null);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || isSending) return;
    const instruction = message.trim();
    setMessage('');
    setIsSending(true);

    await onSend(section.id, instruction, attachedText);

    setAttachedFile(null);
    setAttachedText(null);
    setIsSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!section) return null;

  return (
    <div className={`w-80 border-l flex flex-col shrink-0 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">Chat: {section.title}</div>
          <div className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Ask AI to revise this section
          </div>
        </div>
        <button
          onClick={onClose}
          className={`p-1.5 rounded-lg shrink-0 ml-2 transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
        >
          <X size={16} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-auto p-3 space-y-3">
        {chatHistory.length === 0 && (
          <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <p className="text-xs">
              Tell the AI what to change in this section. You can also attach a reference file.
            </p>
          </div>
        )}

        {chatHistory.map((msg, i) => (
          <div key={i} className="space-y-2">
            {/* User message */}
            <div className="flex justify-end">
              <div className={`max-w-[85%] px-3 py-2 rounded-lg text-xs ${
                isDark ? 'bg-blue-900/40 text-blue-200' : 'bg-blue-50 text-blue-800'
              }`}>
                {msg.instruction}
              </div>
            </div>
            {/* AI response */}
            {msg.summary && (
              <div className="flex justify-start">
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-xs ${
                  isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  {msg.summary}
                </div>
              </div>
            )}
          </div>
        ))}

        {isSending && (
          <div className="flex justify-start">
            <div className={`px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${
              isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
            }`}>
              <Loader2 size={12} className="animate-spin" /> Revising section...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Attached file indicator */}
      {attachedFile && (
        <div className={`mx-3 mb-1 px-2 py-1 rounded text-xs flex items-center gap-1 ${
          isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
        }`}>
          <FileText size={10} />
          <span className="truncate flex-1">{attachedFile}</span>
          <button onClick={() => { setAttachedFile(null); setAttachedText(null); }} className="text-gray-400 hover:text-gray-600">
            <X size={10} />
          </button>
        </div>
      )}

      {/* Input */}
      <div className={`px-3 py-3 border-t shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className={`flex items-end gap-2 rounded-lg border px-2 py-1.5 ${
          isDark ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'
        }`}>
          <button
            onClick={() => fileRef.current?.click()}
            title="Attach reference file"
            className={`p-1 rounded shrink-0 transition-colors ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
          >
            <Paperclip size={14} />
          </button>
          <input ref={fileRef} type="file" accept=".docx,.pdf,.txt,.csv,.xlsx" className="hidden" onChange={handleFileAttach} />

          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Tell AI what to change..."
            rows={1}
            className={`flex-1 text-xs resize-none bg-transparent border-none focus:outline-none py-1 max-h-20 ${
              isDark ? 'text-gray-200 placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
            }`}
            style={{ minHeight: '24px' }}
          />

          <button
            onClick={handleSend}
            disabled={!message.trim() || isSending}
            className={`p-1 rounded shrink-0 transition-colors ${
              !message.trim() || isSending
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-blue-500 hover:text-blue-600'
            }`}
          >
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
